import { CommonModule } from '@angular/common';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  NgZone,
  OnInit,
  ViewChild,
  inject
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { IonIcon, IonSpinner } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  addOutline,
  arrowRedoOutline,
  arrowUndoOutline,
  chevronForwardOutline,
  colorPaletteOutline,
  closeOutline,
  copyOutline,
  desktopOutline,
  eyeOutline,
  imageOutline,
  layersOutline,
  phonePortraitOutline,
  pricetagOutline,
  pricetagsOutline,
  radioButtonOnOutline,
  saveOutline,
  scanOutline,
  sparklesOutline,
  sunnyOutline,
  tabletPortraitOutline,
  textOutline,
  moonOutline
} from 'ionicons/icons';
import { startWith } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ComponentStudioService } from '../../services/component-studio.service';
import { AppContextMenuService } from '../../services/app-context-menu.service';
import { ComponentCanvasThemeService } from '../../services/component-canvas-theme.service';
import { AppContextMenuItem } from '../../components/app-context-menu/app-context-menu.models';
import { ApiLoadErrorComponent } from '../../components/api-load-error/api-load-error.component';
import { InfoTooltipComponent } from '../../components/info-tooltip/info-tooltip.component';
import { PendingBuilderSaveAware } from '../../guards/pending-builder-save.guard';
import { mapCanvasThemeDefaults } from '../../utils/canvas-theme-style.utils';
import {
  CANVAS_THEME_FIELDS,
  CanvasThemeMode,
  CanvasThemePackage
} from '../../models/component-canvas-theme.models';
import {
  AdaptiveGridThemeMode,
  AdaptiveViewportGridContext,
  AdaptiveViewportGridRenderer
} from '../../utils/adaptive-viewport-grid-renderer';
import { ViewportRulerRenderer, ViewportRulerRendererContext } from '../../utils/viewport-ruler-renderer';
import {
  StudioInsertPreset,
  StudioNode,
  StudioNodeType,
  buildStudioNodeStyles,
  getStudioNodeIcon,
  isStudioContainerNode,
  studioDropListId
} from '../../models/component-studio.models';

type PreviewMode = 'responsive' | 'mobile-s' | 'mobile-m' | 'mobile-l' | 'tablet' | 'laptop' | 'laptop-lg' | 'desktop' | '4k';
type ViewportResizeDirection = 'e' | 's' | 'w' | 'se' | 'sw';

interface PreviewPreset {
  id: PreviewMode;
  label: string;
  shortLabel: string;
  icon: string;
  width: number;
  height: number;
}

@Component({
  selector: 'app-component-builder-page',
  standalone: true,
  imports: [CommonModule, FormsModule, DragDropModule, IonIcon, IonSpinner, RouterLink, ApiLoadErrorComponent, InfoTooltipComponent],
  templateUrl: './component-builder-page.component.html',
  styleUrls: ['./component-builder-page.component.scss']
})
export class ComponentBuilderPageComponent implements OnInit, AfterViewInit, PendingBuilderSaveAware {
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly canvasViewportStorageKey = 'bw.component-studio.viewport';
  private readonly defaultCanvasWidth = 1180;
  private readonly defaultCanvasHeight = 760;
  private readonly minCanvasWidth = 1;
  private readonly maxCanvasWidth = 100000;
  private readonly minCanvasHeight = 1;
  private readonly maxCanvasHeight = 100000;
  private readonly canvasViewportFitPaddingX = 4;
  private readonly canvasViewportFitPaddingY = 16;
  private readonly canvasViewportFrameChromeWidthRem = 1.7;
  private readonly canvasViewportFrameChromeHeightRem = 1.7;
  private readonly canvasViewportHandleOverhangWidthRem = 0.96;
  private readonly canvasViewportHandleOverhangHeightRem = 0.96;
  private readonly canvasViewportRulerThicknessRem = 1;

  @ViewChild('canvasViewportShell') private canvasViewportShellRef?: ElementRef<HTMLDivElement>;

  readonly studioService = this.componentStudioService;
  readonly canvasThemeService = this.componentCanvasThemeService;
  readonly canvasThemeFields = CANVAS_THEME_FIELDS;
  readonly previewPresets: PreviewPreset[] = [
    { id: 'mobile-s', label: 'Mobile S', shortLabel: 'S', icon: 'phone-portrait-outline', width: 320, height: 568 },
    { id: 'mobile-m', label: 'Mobile M', shortLabel: 'M', icon: 'phone-portrait-outline', width: 375, height: 667 },
    { id: 'mobile-l', label: 'Mobile L', shortLabel: 'L', icon: 'phone-portrait-outline', width: 430, height: 932 },
    { id: 'tablet', label: 'Tablet', shortLabel: 'Tab', icon: 'tablet-portrait-outline', width: 834, height: 1112 },
    { id: 'laptop', label: 'Laptop', shortLabel: 'Lap', icon: 'desktop-outline', width: 1280, height: 800 },
    { id: 'laptop-lg', label: 'Laptop large', shortLabel: 'L+', icon: 'desktop-outline', width: 1440, height: 900 },
    { id: 'desktop', label: 'Desktop', shortLabel: 'Desk', icon: 'desktop-outline', width: 1600, height: 960 },
    { id: '4k', label: '4K', shortLabel: '4K', icon: 'desktop-outline', width: 2560, height: 1440 }
  ];
  readonly zoomPresetOptions = [50, 75, 90, 100, 110, 125, 150, 175, 200];

  previewMode: PreviewMode = 'laptop';
  showPresetModal = false;
  showLayersDrawer = false;
  showThemeManagerModal = false;
  showPreviewModal = false;
  readonly layersPanelSections = {
    selection: true,
    tree: true
  };
  canvasWidth = this.defaultCanvasWidth;
  canvasHeight = this.defaultCanvasHeight;
  canvasFitScale = 1;
  canvasScale = 1;
  canvasZoomPercentage = 100;
  canvasViewportStyle: Record<string, string> = {};
  canvasViewportFrameStyle: Record<string, string> = {};
  scaledCanvasViewportStyle: Record<string, string> = {};
  gridStyle: Record<string, string> = {};
  topRulerCanvasStyle: Record<string, string> = {};
  leftRulerCanvasStyle: Record<string, string> = {};
  showViewportGrid = false;
  showViewportRulers = false;
  canvasViewportScrollLeft = 0;
  canvasViewportScrollTop = 0;
  showZoomPresetMenu = false;
  isLoading = true;
  loadError: string | null = null;
  editingThemePackageId = 'default';
  themeDraft: CanvasThemePackage | null = null;

  private resizingViewport = false;
  private viewportResizeDirection: ViewportResizeDirection = 'se';
  private resizeStartX = 0;
  private resizeStartY = 0;
  private resizeStartWidth = 0;
  private resizeStartHeight = 0;
  private resizeStartScale = 1;
  private requestedComponentId: string | null = null;
  private loadCycle = 0;
  private loadSettlementTimer: ReturnType<typeof setTimeout> | null = null;
  private canvasViewportResizeObserver?: ResizeObserver;
  private canvasViewportScrollbarMeasureFrame: number | null = null;
  private canvasViewportScrollbarWidth = 0;
  private preferredCanvasWidth = this.defaultCanvasWidth;
  private preferredCanvasHeight = this.defaultCanvasHeight;
  private canvasWidthFollowsAvailableSpace = false;
  private canvasHeightFollowsAvailableSpace = false;
  private autoFitPresetZoom = this.previewMode !== 'responsive';
  private readonly collapsedLayerNodeIds = new Set<string>();
  private readonly adaptiveGridRenderer = new AdaptiveViewportGridRenderer(() => this.getAdaptiveGridContext());
  private readonly viewportRulerRenderer = new ViewportRulerRenderer(() => this.getViewportRulerContext());

  constructor(
    private readonly componentStudioService: ComponentStudioService,
    private readonly componentCanvasThemeService: ComponentCanvasThemeService,
    private readonly contextMenuService: AppContextMenuService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {
    addIcons({
      addOutline,
      arrowRedoOutline,
      arrowUndoOutline,
      chevronForwardOutline,
      colorPaletteOutline,
      closeOutline,
      copyOutline,
      desktopOutline,
      eyeOutline,
      imageOutline,
      layersOutline,
      phonePortraitOutline,
      pricetagOutline,
      pricetagsOutline,
      radioButtonOnOutline,
      saveOutline,
      scanOutline,
      sparklesOutline,
      sunnyOutline,
      tabletPortraitOutline,
      textOutline,
      moonOutline
    });

    this.restoreStoredViewport();
    this.destroyRef.onDestroy(() => this.clearLoadSettlementTimer());
    this.destroyRef.onDestroy(() => this.canvasViewportResizeObserver?.disconnect());
    this.destroyRef.onDestroy(() => this.adaptiveGridRenderer.disconnect());
    this.destroyRef.onDestroy(() => this.viewportRulerRenderer.disconnect());
    this.destroyRef.onDestroy(() => {
      if (this.canvasViewportScrollbarMeasureFrame !== null && typeof cancelAnimationFrame !== 'undefined') {
        cancelAnimationFrame(this.canvasViewportScrollbarMeasureFrame);
      }
    });

    this.route.queryParamMap
      .pipe(startWith(this.route.snapshot.queryParamMap), takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.requestedComponentId = params.get('component');
        if (this.componentStudioService.isLoaded()) {
          this.applyRequestedComponentSelection();
        }
      });
  }

  ngOnInit(): void {
    this.loadBuilderData();
  }

  ngAfterViewInit(): void {
    this.setupCanvasViewportObserver();
    queueMicrotask(() => {
      this.clampCanvasViewport();
      this.refreshCanvasViewportPresentation();
    });
  }

  loadBuilderData(force = false): void {
    const loadCycle = ++this.loadCycle;
    this.clearLoadSettlementTimer();
    this.isLoading = true;
    this.loadError = null;

    this.componentStudioService.loadState(force)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.runInAngular(() => {
            this.applyRequestedComponentSelection();
            this.scheduleLoadSettlement(loadCycle, () => {
              this.isLoading = false;
              queueMicrotask(() => {
                this.clampCanvasViewport();
                this.refreshCanvasViewportPresentation();
              });
            });
          });
        },
        error: () => {
          this.runInAngular(() => {
            this.scheduleLoadSettlement(loadCycle, () => {
              this.loadError = this.componentStudioService.loadError()
                || 'We could not load the component builder right now.';
              this.isLoading = false;
            });
          });
        }
      });

    this.componentCanvasThemeService.loadPackages(force)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.runInAngular(() => {
            if (!this.showThemeManagerModal) {
              return;
            }

            this.loadThemeDraft(this.currentCanvasThemePackageId);
          });
        },
        error: () => {
          // Theme packages are non-blocking for the builder surface because the default canvas theme always exists locally.
        }
      });
  }

  get connectedDropListIds(): string[] {
    return this.componentStudioService.dropListIds();
  }

  get canvasShellStyle(): Record<string, string> {
    return this.canvasThemeService.buildCanvasVariableStyle();
  }

  get currentCanvasThemePackageId(): string {
    return this.canvasThemeService.selectedPackageId();
  }

  get currentCanvasThemeMode(): CanvasThemeMode {
    return this.canvasThemeService.previewMode();
  }

  get hasPendingBuilderChanges(): boolean {
    return this.studioService.hasPendingChanges() || this.canvasThemeService.hasPendingChanges();
  }

  get isSavingBuilderState(): boolean {
    return this.studioService.isSaving() || this.canvasThemeService.isSaving();
  }

  get insertionTargetLabel(): string {
    const selectedNode = this.componentStudioService.selectedNode();
    if (selectedNode && this.isContainer(selectedNode)) {
      return selectedNode.label;
    }

    return this.componentStudioService.selectedParentNode()?.label ?? this.componentStudioService.activeDefinition()?.root.label ?? 'Canvas root';
  }

  get selectedNode(): StudioNode | null {
    return this.componentStudioService.selectedNode();
  }

  setPreviewMode(mode: PreviewMode): void {
    this.previewMode = mode;
    if (mode === 'responsive') {
      this.autoFitPresetZoom = false;
      this.canvasWidthFollowsAvailableSpace = false;
      this.canvasHeightFollowsAvailableSpace = false;
      this.persistViewport();
      this.clampCanvasViewport();
      this.refreshCanvasViewportPresentation();
      return;
    }

    const preset = this.previewPresets.find((item) => item.id === mode);
    if (!preset) {
      return;
    }

    this.canvasWidthFollowsAvailableSpace = false;
    this.canvasHeightFollowsAvailableSpace = false;
    this.preferredCanvasWidth = clamp(preset.width, this.minCanvasWidth, this.maxCanvasWidth);
    this.autoFitPresetZoom = true;
    this.syncPresetZoomToAvailableWidth();
    this.persistViewport();
    this.clampCanvasViewport();
    this.refreshCanvasViewportPresentation();
  }

  updateViewportWidth(value: string | number): void {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return;
    }

    this.previewMode = 'responsive';
    this.autoFitPresetZoom = false;
    this.canvasWidthFollowsAvailableSpace = false;
    this.preferredCanvasWidth = clamp(Math.round(parsed), this.minCanvasWidth, this.maxCanvasWidth);
    this.persistViewport();
    this.clampCanvasViewport();
    this.refreshCanvasViewportPresentation();
  }

  updateViewportHeight(value: string | number): void {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return;
    }

    this.previewMode = 'responsive';
    this.autoFitPresetZoom = false;
    this.canvasHeightFollowsAvailableSpace = false;
    this.preferredCanvasHeight = clamp(Math.round(parsed), this.minCanvasHeight, this.maxCanvasHeight);
    this.persistViewport();
    this.clampCanvasViewport();
    this.refreshCanvasViewportPresentation();
  }

  updateViewportZoom(value: string | number): void {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return;
    }

    this.autoFitPresetZoom = false;
    this.canvasZoomPercentage = clamp(Math.round(parsed), 5, 300);
    if (this.previewMode === 'responsive') {
      const bounds = this.getCanvasResizeBounds();
      this.preferredCanvasWidth = Math.min(this.preferredCanvasWidth, bounds.width);
      this.preferredCanvasHeight = Math.min(this.preferredCanvasHeight, bounds.height);
    }
    this.persistViewport();
    this.clampCanvasViewport();
    this.refreshCanvasViewportPresentation();
  }

  nudgeViewportZoom(delta: number): void {
    this.updateViewportZoom(this.canvasZoomPercentage + delta);
  }

  selectViewportZoomPreset(value: string | number): void {
    this.updateViewportZoom(value);
    this.showZoomPresetMenu = false;
  }

  openViewportZoomMenu(): void {
    this.showZoomPresetMenu = true;
  }

  toggleViewportGrid(): void {
    this.showViewportGrid = !this.showViewportGrid;
    this.persistViewport();
    if (!this.showViewportGrid) {
      this.adaptiveGridRenderer.disconnect();
      return;
    }

    queueMicrotask(() => this.refreshCanvasViewportPresentation());
  }

  toggleViewportRulers(): void {
    this.showViewportRulers = !this.showViewportRulers;
    this.persistViewport();
    if (!this.showViewportRulers) {
      this.viewportRulerRenderer.disconnect();
      this.refreshCanvasViewportPresentation();
      return;
    }

    queueMicrotask(() => this.refreshCanvasViewportPresentation());
  }

  handleCanvasViewportScroll(event: Event): void {
    const viewport = event.target as HTMLDivElement | null;
    this.canvasViewportScrollLeft = viewport?.scrollLeft ?? 0;
    this.canvasViewportScrollTop = viewport?.scrollTop ?? 0;
    this.refreshCanvasChromePresentation();
  }

  maximizeViewportWidth(): void {
    this.previewMode = 'responsive';
    this.autoFitPresetZoom = false;
    this.canvasWidthFollowsAvailableSpace = true;
    this.preferredCanvasWidth = this.getCanvasResizeBounds().width;
    this.persistViewport();
    this.clampCanvasViewport();
    this.refreshCanvasViewportPresentation();
  }

  maximizeViewportHeight(): void {
    this.previewMode = 'responsive';
    this.autoFitPresetZoom = false;
    this.canvasHeightFollowsAvailableSpace = true;
    this.preferredCanvasHeight = this.getCanvasResizeBounds().height;
    this.persistViewport();
    this.clampCanvasViewport();
    this.refreshCanvasViewportPresentation();
  }

  maximizeViewport(): void {
    this.previewMode = 'responsive';
    this.autoFitPresetZoom = false;
    const bounds = this.getCanvasResizeBounds();
    this.canvasWidthFollowsAvailableSpace = true;
    this.canvasHeightFollowsAvailableSpace = true;
    this.preferredCanvasWidth = bounds.width;
    this.preferredCanvasHeight = bounds.height;
    this.persistViewport();
    this.clampCanvasViewport();
    this.refreshCanvasViewportPresentation();
  }

  toggleLayersDrawer(): void {
    this.showLayersDrawer = !this.showLayersDrawer;
    if (this.showLayersDrawer) {
      this.closePresetModal();
      this.contextMenuService.close();
      this.expandLayerPath(this.componentStudioService.selectedNodeId() ?? this.componentStudioService.activeDefinition()?.root.id ?? null);
    }
    queueMicrotask(() => {
      this.clampCanvasViewport();
      this.refreshCanvasViewportPresentation();
    });
  }

  closeLayersDrawer(): void {
    this.showLayersDrawer = false;
    queueMicrotask(() => {
      this.clampCanvasViewport();
      this.refreshCanvasViewportPresentation();
    });
  }

  openPresetModal(): void {
    this.closeLayersDrawer();
    this.closeThemeManagerModal();
    this.closePreviewModal();
    this.showPresetModal = true;
    this.contextMenuService.close();
  }

  closePresetModal(): void {
    this.showPresetModal = false;
  }

  openThemeManagerModal(): void {
    this.closeLayersDrawer();
    this.closePresetModal();
    this.closePreviewModal();
    this.showThemeManagerModal = true;
    this.contextMenuService.close();
    this.loadThemeDraft(this.currentCanvasThemePackageId);
  }

  closeThemeManagerModal(): void {
    this.showThemeManagerModal = false;
  }

  openPreviewModal(): void {
    this.closeLayersDrawer();
    this.closePresetModal();
    this.closeThemeManagerModal();
    this.showPreviewModal = true;
    this.contextMenuService.close();
  }

  closePreviewModal(): void {
    this.showPreviewModal = false;
  }

  async flushPendingChanges(): Promise<void> {
    await Promise.all([
      this.componentStudioService.flushPendingSave(),
      this.canvasThemeService.flushPendingSave()
    ]);
  }

  selectCanvasThemePackage(packageId: string): void {
    this.canvasThemeService.setSelectedPackage(packageId);
  }

  setCanvasThemeMode(mode: CanvasThemeMode): void {
    this.canvasThemeService.setPreviewMode(mode);
    this.refreshCanvasViewportPresentation();
  }

  chooseThemeDraft(packageId: string): void {
    this.loadThemeDraft(packageId);
  }

  createThemePackage(): void {
    const createdPackage = this.canvasThemeService.createPackage();
    this.editingThemePackageId = createdPackage.id;
    this.themeDraft = createdPackage;
  }

  saveThemePackage(): void {
    if (!this.themeDraft) {
      return;
    }

    this.canvasThemeService.savePackage(this.themeDraft);
    this.canvasThemeService.setSelectedPackage(this.themeDraft.id);
    this.loadThemeDraft(this.themeDraft.id);
  }

  deleteThemePackage(): void {
    if (!this.themeDraft || this.themeDraft.id === 'default') {
      return;
    }

    this.canvasThemeService.deletePackage(this.themeDraft.id);
    this.loadThemeDraft(this.canvasThemeService.selectedPackageId());
  }

  updateThemeDraftMeta(field: 'name' | 'description', value: string): void {
    if (!this.themeDraft || this.themeDraft.id === 'default') {
      return;
    }

    this.themeDraft = {
      ...this.themeDraft,
      [field]: value
    };
  }

  updateThemeDraftOverride(mode: CanvasThemeMode, key: keyof NonNullable<CanvasThemePackage['lightOverrides']>, value: string): void {
    if (!this.themeDraft || this.themeDraft.id === 'default') {
      return;
    }

    const normalizedValue = value.trim();
    const targetOverrides = {
      ...(mode === 'dark' ? this.themeDraft.darkOverrides : this.themeDraft.lightOverrides)
    };

    if (normalizedValue) {
      targetOverrides[key] = normalizedValue;
    } else {
      delete targetOverrides[key];
    }

    this.themeDraft = {
      ...this.themeDraft,
      ...(mode === 'dark'
        ? { darkOverrides: targetOverrides }
        : { lightOverrides: targetOverrides })
    };
  }

  createNewComponent(): void {
    const componentId = this.componentStudioService.createBlankDefinition();
    this.navigateToComponent(componentId);
  }

  duplicateCurrentComponent(): void {
    const activeDefinition = this.componentStudioService.activeDefinition();
    if (!activeDefinition) {
      return;
    }

    const duplicatedId = this.componentStudioService.duplicateDefinition(activeDefinition.id);
    if (duplicatedId) {
      this.navigateToComponent(duplicatedId);
    }
  }

  saveVersion(): void {
    this.componentStudioService.saveActiveDefinitionVersion();
  }

  updateComponentName(value: string): void {
    this.componentStudioService.renameActiveDefinition(value);
  }

  updateDescription(value: string): void {
    this.componentStudioService.updateActiveDefinitionDescription(value);
  }

  updateStatus(value: string): void {
    if (value === 'draft' || value === 'ready') {
      this.componentStudioService.updateActiveDefinitionStatus(value);
    }
  }

  insertPreset(presetId: string, targetParentId?: string | null): void {
    this.componentStudioService.insertPreset(presetId, targetParentId);
    this.showPresetModal = false;
  }

  selectNode(nodeId: string, event?: Event): void {
    event?.stopPropagation();
    this.contextMenuService.close();
    this.componentStudioService.selectNode(nodeId);
    this.expandLayerPath(nodeId);
  }

  selectLayerNode(nodeId: string, event?: Event): void {
    this.selectNode(nodeId, event);
    this.scrollCanvasNodeIntoView(nodeId);
  }

  handleNodeDrop(event: CdkDragDrop<StudioNode[]>, targetParentId: string): void {
    const nodeId = event.item.data as string;
    this.componentStudioService.moveNode(nodeId, targetParentId, event.currentIndex);
    this.componentStudioService.selectNode(nodeId);
  }

  nodeStyles(node: StudioNode): Record<string, string | number> {
    const styles = buildStudioNodeStyles(node);
    const activeDefinition = this.componentStudioService.activeDefinition();
    mapCanvasThemeDefaults(styles);

    if (activeDefinition?.root.id === node.id) {
      // Root node styles are applied inline, so compensate there for the
      // viewport's vertical scrollbar width instead of relying on SCSS alone.
      styles['width'] = 'calc(100% - var(--bw-viewport-scrollbar-width, 0px))';
      styles['min-width'] = '0';
      styles['box-sizing'] = 'border-box';

      if (typeof styles['min-height'] === 'string') {
        styles['min-height'] = `max(${styles['min-height']}, 100%)`;
      }
    }

    return styles;
  }

  nodeIcon(nodeType: StudioNodeType): string {
    return getStudioNodeIcon(nodeType);
  }

  isContainer(node: StudioNode): boolean {
    return isStudioContainerNode(node);
  }

  dropListId(nodeId: string): string {
    return studioDropListId(nodeId);
  }

  layerPadding(depth: number): string {
    return `${0.35 + depth * 0.72}rem`;
  }

  toggleLayersPanelSection(section: 'selection' | 'tree'): void {
    this.layersPanelSections[section] = !this.layersPanelSections[section];
  }

  hasLayerChildren(node: StudioNode): boolean {
    return node.children.length > 0;
  }

  isLayerExpanded(nodeId: string): boolean {
    return !this.collapsedLayerNodeIds.has(nodeId);
  }

  toggleLayerExpansion(nodeId: string, event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    if (this.collapsedLayerNodeIds.has(nodeId)) {
      this.collapsedLayerNodeIds.delete(nodeId);
      return;
    }

    this.collapsedLayerNodeIds.add(nodeId);
  }

  trackNode(_index: number, node: StudioNode): string {
    return node.id;
  }

  trackPreset(_index: number, preset: StudioInsertPreset): string {
    return preset.id;
  }

  openCanvasContextMenu(event: MouseEvent, node: StudioNode): void {
    event.preventDefault();
    event.stopPropagation();
    this.componentStudioService.selectNode(node.id);
    this.contextMenuService.openAt(event, this.buildNodeContextMenuItems(node), node.label);
  }

  openLayerContextMenu(event: MouseEvent, node: StudioNode): void {
    event.preventDefault();
    event.stopPropagation();
    this.componentStudioService.selectNode(node.id);
    this.contextMenuService.openAt(event, this.buildNodeContextMenuItems(node), `${node.label} layer`);
  }

  openDefinitionContextMenu(event: MouseEvent): void {
    if (this.hasNativeContextTarget(event)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.contextMenuService.openAt(event, [
      {
        id: 'new-component',
        label: 'New component',
        icon: 'add-outline',
        action: () => this.createNewComponent()
      },
      {
        id: 'duplicate-component',
        label: 'Duplicate component',
        icon: 'copy-outline',
        action: () => this.duplicateCurrentComponent()
      },
      {
        id: 'save-component',
        label: 'Save version',
        icon: 'create-outline',
        action: () => this.saveVersion()
      },
      {
        id: 'open-layers',
        label: 'Open layers panel',
        icon: 'open-outline',
        action: () => {
          this.showLayersDrawer = true;
          this.closePresetModal();
        }
      },
      {
        id: 'open-presets',
        label: 'Open presets',
        icon: 'add-outline',
        action: () => this.openPresetModal()
      },
      {
        id: 'open-theme-manager',
        label: 'Manage canvas themes',
        icon: 'create-outline',
        action: () => this.openThemeManagerModal()
      },
      {
        id: 'open-preview',
        label: 'Open preview',
        icon: 'eye-outline',
        action: () => this.openPreviewModal()
      }
    ], 'Component actions');
  }

  openWorkspaceContextMenu(event: MouseEvent): void {
    if (this.hasNativeContextTarget(event)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    if ((event.target as HTMLElement | null)?.closest('.component-builder__node, .component-builder__layer-button, .component-builder__preset-card')) {
      return;
    }

    this.contextMenuService.openAt(event, [
      {
        id: 'workspace-presets',
        label: 'Add preset',
        icon: 'add-outline',
        action: () => this.openPresetModal()
      },
      {
        id: 'workspace-layers',
        label: 'Open layers panel',
        icon: 'open-outline',
        action: () => this.toggleLayersDrawer()
      },
      {
        id: 'workspace-save',
        label: 'Save version',
        icon: 'create-outline',
        action: () => this.saveVersion()
      },
      {
        id: 'workspace-theme',
        label: 'Manage canvas themes',
        icon: 'create-outline',
        action: () => this.openThemeManagerModal()
      },
      {
        id: 'workspace-preview',
        label: 'Open preview',
        icon: 'eye-outline',
        action: () => this.openPreviewModal()
      }
    ], 'Builder workspace');
  }

  openPresetContextMenu(event: MouseEvent, preset: StudioInsertPreset): void {
    event.preventDefault();
    event.stopPropagation();
    this.contextMenuService.openAt(event, [
      {
        id: `${preset.id}-selected`,
        label: `Insert into ${this.insertionTargetLabel}`,
        icon: 'add-outline',
        action: () => this.insertPreset(preset.id)
      },
      {
        id: `${preset.id}-root`,
        label: 'Insert at canvas root',
        icon: 'open-outline',
        action: () => {
          const rootId = this.componentStudioService.activeDefinition()?.root.id;
          if (rootId) {
            this.insertPreset(preset.id, rootId);
          }
        }
      }
    ], preset.label);
  }

  startViewportResize(event: PointerEvent, direction: ViewportResizeDirection = 'se'): void {
    event.preventDefault();
    event.stopPropagation();

    if (event.detail > 1) {
      if (direction === 's') {
        this.maximizeViewportHeight();
        return;
      }

      if (direction === 'e' || direction === 'w') {
        this.maximizeViewportWidth();
        return;
      }
    }

    this.resizingViewport = true;
    this.viewportResizeDirection = direction;
    this.resizeStartX = event.clientX;
    this.resizeStartY = event.clientY;
    this.resizeStartWidth = this.canvasWidth;
    this.resizeStartHeight = this.canvasHeight;
    this.resizeStartScale = this.canvasScale || 1;
  }

  @HostListener('document:pointermove', ['$event'])
  handlePointerMove(event: PointerEvent): void {
    if (!this.resizingViewport) {
      return;
    }

    const deltaX = (event.clientX - this.resizeStartX) / this.resizeStartScale;
    const deltaY = (event.clientY - this.resizeStartY) / this.resizeStartScale;
    let nextWidth = this.resizeStartWidth;
    let nextHeight = this.resizeStartHeight;
    const bounds = this.getCanvasResizeBounds();

    if (this.viewportResizeDirection.includes('e')) {
      nextWidth += deltaX * 2;
    }

    if (this.viewportResizeDirection.includes('w')) {
      nextWidth -= deltaX * 2;
    }

    if (this.viewportResizeDirection.includes('s')) {
      nextHeight += deltaY;
    }

    this.previewMode = 'responsive';
    this.autoFitPresetZoom = false;
    if (this.viewportResizeDirection.includes('e') || this.viewportResizeDirection.includes('w')) {
      this.canvasWidthFollowsAvailableSpace = false;
    }
    if (this.viewportResizeDirection.includes('s')) {
      this.canvasHeightFollowsAvailableSpace = false;
    }
    this.preferredCanvasWidth = clamp(Math.round(nextWidth), this.minCanvasWidth, this.maxCanvasWidth);
    this.preferredCanvasHeight = clamp(Math.round(nextHeight), this.minCanvasHeight, this.maxCanvasHeight);
    this.canvasWidth = clamp(this.preferredCanvasWidth, this.minCanvasWidth, bounds.width);
    this.canvasHeight = clamp(this.preferredCanvasHeight, this.minCanvasHeight, bounds.height);
    this.refreshCanvasViewportPresentation();
  }

  @HostListener('document:pointerup')
  handlePointerUp(): void {
    if (!this.resizingViewport) {
      return;
    }

    this.resizingViewport = false;
    this.persistViewport();
  }

  @HostListener('document:pointerdown', ['$event'])
  handleDocumentPointerDown(event: PointerEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('.component-builder__viewport-combo')) {
      return;
    }

    this.showZoomPresetMenu = false;
  }

  handleViewportZoomFocusOut(event: FocusEvent): void {
    const target = event.relatedTarget as HTMLElement | null;
    if (target?.closest('.component-builder__viewport-combo')) {
      return;
    }

    this.showZoomPresetMenu = false;
  }

  @HostListener('window:resize')
  handleWindowResize(): void {
    this.clampCanvasViewport();
    this.refreshCanvasViewportPresentation();
  }

  @HostListener('window:beforeunload')
  @HostListener('window:pagehide')
  handleBeforeUnload(): void {
    this.componentStudioService.flushPendingSaveOnUnload();
    this.canvasThemeService.flushPendingSaveOnUnload();
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardShortcuts(event: KeyboardEvent): void {
    if (this.shouldIgnoreShortcut(event)) {
      return;
    }

    const hasCommandModifier = event.ctrlKey || event.metaKey;
    if (!hasCommandModifier || event.altKey) {
      return;
    }

    const key = event.key.toLowerCase();
    if (key === 'z' && event.shiftKey) {
      if (this.componentStudioService.redo()) {
        event.preventDefault();
      }
      return;
    }

    if (key === 'z') {
      if (this.componentStudioService.undo()) {
        event.preventDefault();
      }
      return;
    }

    if (key === 'y' && this.componentStudioService.redo()) {
      event.preventDefault();
    }
  }

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    this.closePresetModal();
    this.closeLayersDrawer();
    this.closeThemeManagerModal();
    this.closePreviewModal();
  }

  private navigateToComponent(componentId: string): void {
    this.componentStudioService.setActiveDefinition(componentId);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { component: componentId },
      queryParamsHandling: 'merge'
    });
  }

  private buildNodeContextMenuItems(node: StudioNode): AppContextMenuItem[] {
    const canDelete = !!this.componentStudioService.selectedParentNode();
    const items: AppContextMenuItem[] = [
      {
        id: 'select',
        label: 'Select node',
        icon: 'create-outline',
        action: () => this.componentStudioService.selectNode(node.id)
      },
      {
        id: 'duplicate',
        label: 'Duplicate node',
        icon: 'copy-outline',
        action: () => {
          this.componentStudioService.selectNode(node.id);
          this.componentStudioService.duplicateSelectedNode();
        }
      },
      {
        id: node.hidden ? 'show' : 'hide',
        label: node.hidden ? 'Show node' : 'Hide node',
        icon: node.hidden ? 'eye-outline' : 'eye-off-outline',
        action: () => {
          this.componentStudioService.selectNode(node.id);
          this.componentStudioService.toggleSelectedHidden();
        }
      },
      {
        id: node.locked ? 'unlock' : 'lock',
        label: node.locked ? 'Unlock node' : 'Lock node',
        icon: node.locked ? 'lock-open-outline' : 'lock-closed-outline',
        action: () => {
          this.componentStudioService.selectNode(node.id);
          this.componentStudioService.toggleSelectedLocked();
        }
      }
    ];

    if (this.isContainer(node)) {
      items.splice(1, 0, {
        id: 'insert-text',
        label: 'Add preset here',
        icon: 'add-outline',
        action: () => {
          this.componentStudioService.selectNode(node.id);
          this.openPresetModal();
        }
      });
    }

    items.push({
      id: 'delete',
      label: 'Delete node',
      icon: 'trash-outline',
      tone: 'danger',
      disabled: !canDelete,
      action: () => {
        this.componentStudioService.selectNode(node.id);
        this.componentStudioService.removeSelectedNode();
      }
    });

    return items;
  }

  private expandLayerPath(nodeId: string | null): void {
    if (!nodeId) {
      return;
    }

    const root = this.componentStudioService.activeDefinition()?.root;
    if (!root) {
      return;
    }

    const path = this.findNodePath(root, nodeId);
    if (!path) {
      return;
    }

    for (const ancestor of path.slice(0, Math.max(path.length - 1, 1))) {
      this.collapsedLayerNodeIds.delete(ancestor.id);
    }
  }

  private findNodePath(root: StudioNode, nodeId: string, trail: StudioNode[] = []): StudioNode[] | null {
    const nextTrail = [...trail, root];
    if (root.id === nodeId) {
      return nextTrail;
    }

    for (const child of root.children) {
      const match = this.findNodePath(child, nodeId, nextTrail);
      if (match) {
        return match;
      }
    }

    return null;
  }

  private restoreStoredViewport(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const storedViewport = JSON.parse(window.localStorage.getItem(this.canvasViewportStorageKey) || 'null') as {
        mode?: PreviewMode;
        width?: number;
        height?: number;
        zoom?: number;
        fitWidth?: boolean;
        fitHeight?: boolean;
        showGrid?: boolean;
        showRulers?: boolean;
      } | null;

      if (!storedViewport) {
        return;
      }

      const storedMode = storedViewport.mode;
      if (
        storedMode === 'responsive'
        || storedMode === 'mobile-s'
        || storedMode === 'mobile-m'
        || storedMode === 'mobile-l'
        || storedMode === 'tablet'
        || storedMode === 'laptop'
        || storedMode === 'laptop-lg'
        || storedMode === 'desktop'
        || storedMode === '4k'
      ) {
        this.previewMode = storedMode;
        this.autoFitPresetZoom = storedMode !== 'responsive';
      }

      if (typeof storedViewport.width === 'number') {
        this.preferredCanvasWidth = clamp(Math.round(storedViewport.width), this.minCanvasWidth, this.maxCanvasWidth);
        this.canvasWidth = this.preferredCanvasWidth;
      }

      if (typeof storedViewport.height === 'number') {
        this.preferredCanvasHeight = clamp(Math.round(storedViewport.height), this.minCanvasHeight, this.maxCanvasHeight);
        this.canvasHeight = this.preferredCanvasHeight;
      }

      if (typeof storedViewport.zoom === 'number') {
        this.canvasZoomPercentage = clamp(Math.round(storedViewport.zoom), 5, 300);
      }

      this.canvasWidthFollowsAvailableSpace = !!storedViewport.fitWidth;
      this.canvasHeightFollowsAvailableSpace = !!storedViewport.fitHeight;
      this.showViewportGrid = !!storedViewport.showGrid;
      this.showViewportRulers = !!storedViewport.showRulers;
    } catch {
      // Ignore malformed viewport state and fall back to defaults.
    }
  }

  private persistViewport(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(this.canvasViewportStorageKey, JSON.stringify({
      mode: this.previewMode,
      width: this.preferredCanvasWidth,
      height: this.preferredCanvasHeight,
      zoom: this.canvasZoomPercentage,
      fitWidth: this.canvasWidthFollowsAvailableSpace,
      fitHeight: this.canvasHeightFollowsAvailableSpace,
      showGrid: this.showViewportGrid,
      showRulers: this.showViewportRulers
    }));
  }

  private clampCanvasViewport(): void {
    this.syncPresetZoomToAvailableWidth();
    const bounds = this.getCanvasResizeBounds();
    if (this.canvasWidthFollowsAvailableSpace) {
      this.preferredCanvasWidth = bounds.width;
    }
    if (this.canvasHeightFollowsAvailableSpace) {
      this.preferredCanvasHeight = bounds.height;
    }
    this.canvasWidth = clamp(this.preferredCanvasWidth, this.minCanvasWidth, this.maxCanvasWidth);
    this.canvasHeight = clamp(this.preferredCanvasHeight, this.minCanvasHeight, bounds.height);
  }

  private setupCanvasViewportObserver(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const shellElement = this.canvasViewportShellRef?.nativeElement;
    if (!shellElement) {
      return;
    }

    this.canvasViewportResizeObserver?.disconnect();
    this.canvasViewportResizeObserver = new ResizeObserver(() => {
      queueMicrotask(() => {
        this.runInAngular(() => {
          this.clampCanvasViewport();
          this.refreshCanvasViewportPresentation();
        });
      });
    });
    this.canvasViewportResizeObserver.observe(shellElement);
  }

  private refreshCanvasViewportPresentation(): void {
    const { width: availableWidth } = this.getCanvasAvailableSpace();
    const frameChrome = this.getCanvasViewportFrameChromePx();
    const rulerReserve = this.getCanvasViewportRulerReservePx();
    const effectiveWidthSpace = Math.max(1, availableWidth - frameChrome.width - rulerReserve.width);
    const widthFitScale = effectiveWidthSpace / this.canvasWidth;
    const requestedScale = clamp(this.canvasZoomPercentage / 100, 0.05, 3);
    // Width fitting should not collapse just because the window got shorter.
    // Height pressure is handled by logical viewport-height clamping.
    const effectiveScale = clamp(Math.min(widthFitScale, requestedScale), 0.05, 3);
    this.canvasFitScale = clamp(widthFitScale, 0.05, 3);
    this.canvasScale = effectiveScale;
    this.canvasViewportStyle = {
      'color-scheme': this.currentCanvasThemeMode,
      '--bw-viewport-scrollbar-width': `${this.canvasViewportScrollbarWidth}px`,
      width: `${this.canvasWidth}px`,
      height: `${this.canvasHeight}px`
    };
    this.canvasViewportFrameStyle = {
      '--bw-ruler-thickness': `${rulerReserve.width}px`,
      width: `${this.canvasWidth * effectiveScale + this.remToPx(this.canvasViewportFrameChromeWidthRem) + rulerReserve.width}px`,
      height: `${this.canvasHeight * effectiveScale + this.remToPx(this.canvasViewportFrameChromeHeightRem) + rulerReserve.height}px`
    };
    this.scaledCanvasViewportStyle = {
      ...this.canvasViewportStyle,
      transform: `scale(${effectiveScale})`,
      'transform-origin': 'top left'
    };
    this.refreshCanvasChromePresentation();
    this.scheduleCanvasViewportScrollbarMeasurement();
  }

  private refreshCanvasChromePresentation(): void {
    const viewportElement = this.getCanvasViewportElement();
    const gridWidth = viewportElement?.clientWidth ?? this.canvasWidth;
    const gridHeight = viewportElement?.clientHeight ?? this.canvasHeight;
    this.gridStyle = {
      width: `${gridWidth}px`,
      height: `${gridHeight}px`,
      transform: `scale(${this.canvasScale})`,
      'transform-origin': 'top left'
    };
    this.topRulerCanvasStyle = {
      width: `${gridWidth * this.canvasScale}px`
    };
    this.leftRulerCanvasStyle = {
      height: `${Math.max(1, gridHeight * this.canvasScale)}px`
    };
    if (this.showViewportGrid) {
      this.adaptiveGridRenderer.refreshObservation();
      this.adaptiveGridRenderer.scheduleRender();
    } else {
      this.adaptiveGridRenderer.disconnect();
    }
    if (this.showViewportRulers) {
      this.viewportRulerRenderer.scheduleRender();
    } else {
      this.viewportRulerRenderer.disconnect();
    }
  }

  private getCanvasResizeBounds(): { width: number; height: number } {
    const shellElement = this.canvasViewportShellRef?.nativeElement;
    if (!shellElement) {
      return {
        width: this.maxCanvasWidth,
        height: this.maxCanvasHeight
      };
    }

    const requestedScale = clamp(this.canvasZoomPercentage / 100, 0.05, 3);
    const { width: availableWidth, height: availableHeight } = this.getCanvasAvailableSpace();
    const frameChrome = this.getCanvasViewportFrameChromePx();
    const rulerReserve = this.getCanvasViewportRulerReservePx();

    return {
      width: clamp(Math.floor(Math.max(1, availableWidth - frameChrome.width - rulerReserve.width) / requestedScale), this.minCanvasWidth, this.maxCanvasWidth),
      height: clamp(Math.floor(Math.max(1, availableHeight - frameChrome.height - rulerReserve.height) / requestedScale), this.minCanvasHeight, this.maxCanvasHeight)
    };
  }

  private getCanvasAvailableSpace(): { width: number; height: number } {
    const shellElement = this.canvasViewportShellRef?.nativeElement;
    if (!shellElement) {
      return {
        width: this.canvasWidth,
        height: this.canvasHeight
      };
    }

    const shellRect = shellElement.getBoundingClientRect();
    const baseWidth = shellElement.clientWidth || Math.round(shellRect.width) || this.canvasWidth;
    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : Math.round(shellRect.height);
    const baseHeight = Math.max(1, viewportHeight - shellRect.top);

    return {
      width: Math.max(1, baseWidth - this.canvasViewportFitPaddingX),
      height: Math.max(1, baseHeight - this.canvasViewportFitPaddingY)
    };
  }

  private syncPresetZoomToAvailableWidth(): void {
    if (this.previewMode === 'responsive' || !this.autoFitPresetZoom) {
      return;
    }

    const shellElement = this.canvasViewportShellRef?.nativeElement;
    if (!shellElement) {
      return;
    }

    const { width: availableWidth } = this.getCanvasAvailableSpace();
    const frameChrome = this.getCanvasViewportFrameChromePx();
    const rulerReserve = this.getCanvasViewportRulerReservePx();
    const effectiveWidthSpace = Math.max(1, availableWidth - frameChrome.width - rulerReserve.width);
    const fitZoomPercentage = clamp(Math.floor((effectiveWidthSpace / Math.max(1, this.preferredCanvasWidth)) * 100), 5, 100);
    this.canvasZoomPercentage = fitZoomPercentage;
  }

  private getCanvasViewportElement(): HTMLElement | null {
    return this.canvasViewportShellRef?.nativeElement.querySelector('.component-builder__canvas-viewport') ?? null;
  }

  private getNodeBoxElement(nodeId: string): HTMLElement | null {
    return this.canvasViewportShellRef?.nativeElement.querySelector(
      `.component-builder__node[data-node-id="${nodeId}"] > .component-builder__node-box`
    ) ?? null;
  }

  private getCanvasRootElement(): HTMLElement | null {
    return this.canvasViewportShellRef?.nativeElement.querySelector('.component-builder__canvas-root') ?? null;
  }

  private scrollCanvasNodeIntoView(nodeId: string): void {
    queueMicrotask(() => {
      const viewport = this.getCanvasViewportElement();
      const rootId = this.componentStudioService.activeDefinition()?.root.id ?? null;
      const target = nodeId === rootId
        ? this.getCanvasRootElement()
        : this.getNodeBoxElement(nodeId);

      if (!viewport || !target) {
        return;
      }

      const viewportRect = viewport.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const edgePadding = 24;
      let nextScrollLeft = viewport.scrollLeft;
      let nextScrollTop = viewport.scrollTop;

      if (targetRect.left < viewportRect.left + edgePadding) {
        nextScrollLeft += targetRect.left - (viewportRect.left + edgePadding);
      } else if (targetRect.right > viewportRect.right - edgePadding) {
        nextScrollLeft += targetRect.right - (viewportRect.right - edgePadding);
      }

      if (targetRect.top < viewportRect.top + edgePadding) {
        nextScrollTop += targetRect.top - (viewportRect.top + edgePadding);
      } else if (targetRect.bottom > viewportRect.bottom - edgePadding) {
        nextScrollTop += targetRect.bottom - (viewportRect.bottom - edgePadding);
      }

      nextScrollLeft = clamp(Math.round(nextScrollLeft), 0, Math.max(0, viewport.scrollWidth - viewport.clientWidth));
      nextScrollTop = clamp(Math.round(nextScrollTop), 0, Math.max(0, viewport.scrollHeight - viewport.clientHeight));

      viewport.scrollTo({
        left: nextScrollLeft,
        top: nextScrollTop,
        behavior: 'smooth'
      });
    });
  }

  private getGridCanvasElement(): HTMLCanvasElement | null {
    return this.canvasViewportShellRef?.nativeElement.querySelector('.component-builder__viewport-grid') ?? null;
  }

  private getCanvasViewportFrameChromePx(): { width: number; height: number } {
    return {
      width: this.remToPx(this.canvasViewportFrameChromeWidthRem + this.canvasViewportHandleOverhangWidthRem),
      height: this.remToPx(this.canvasViewportFrameChromeHeightRem + this.canvasViewportHandleOverhangHeightRem)
    };
  }

  private getCanvasViewportRulerReservePx(): { width: number; height: number } {
    if (!this.showViewportRulers) {
      return { width: 0, height: 0 };
    }

    const thickness = this.remToPx(this.canvasViewportRulerThicknessRem);
    return {
      width: thickness,
      height: thickness
    };
  }

  private remToPx(rem: number): number {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return rem * 16;
    }

    const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize || '16');
    return rem * (Number.isFinite(rootFontSize) && rootFontSize > 0 ? rootFontSize : 16);
  }

  private scheduleCanvasViewportScrollbarMeasurement(): void {
    if (typeof window === 'undefined' || typeof requestAnimationFrame === 'undefined') {
      return;
    }

    if (this.canvasViewportScrollbarMeasureFrame !== null) {
      cancelAnimationFrame(this.canvasViewportScrollbarMeasureFrame);
    }

    this.canvasViewportScrollbarMeasureFrame = requestAnimationFrame(() => {
      this.canvasViewportScrollbarMeasureFrame = null;
      const viewportElement = this.getCanvasViewportElement();
      if (!viewportElement) {
        return;
      }

      const nextScrollbarWidth = Math.max(0, Math.round(viewportElement.offsetWidth - viewportElement.clientWidth));
      if (nextScrollbarWidth === this.canvasViewportScrollbarWidth) {
        return;
      }

      this.canvasViewportScrollbarWidth = nextScrollbarWidth;
      this.refreshCanvasViewportPresentation();
      this.changeDetectorRef.detectChanges();
    });
  }

  private loadThemeDraft(packageId: string): void {
    this.editingThemePackageId = packageId;
    this.themeDraft = this.canvasThemeService.getPackageForEdit(packageId);
  }

  private applyRequestedComponentSelection(): void {
    if (this.requestedComponentId) {
      this.componentStudioService.setActiveDefinition(this.requestedComponentId);
      return;
    }

    const activeDefinition = this.componentStudioService.activeDefinition();
    if (activeDefinition) {
      this.componentStudioService.setActiveDefinition(activeDefinition.id);
    }
  }

  private scheduleLoadSettlement(loadCycle: number, settle: () => void): void {
    this.clearLoadSettlementTimer();
    this.loadSettlementTimer = setTimeout(() => {
      if (loadCycle !== this.loadCycle) {
        return;
      }

      this.runInAngular(() => {
        settle();
        this.loadSettlementTimer = null;
      });
    }, 0);
  }

  private clearLoadSettlementTimer(): void {
    if (!this.loadSettlementTimer) {
      return;
    }

    clearTimeout(this.loadSettlementTimer);
    this.loadSettlementTimer = null;
  }

  private hasNativeContextTarget(event: MouseEvent): boolean {
    const target = event.target as HTMLElement | null;
    return !!target?.closest('input, textarea, select, [contenteditable="true"]');
  }

  private shouldIgnoreShortcut(event: KeyboardEvent): boolean {
    const target = event.target as HTMLElement | null;
    return !!target?.closest('input, textarea, select, [contenteditable="true"]');
  }

  private runInAngular(callback: () => void): void {
    this.ngZone.run(() => {
      callback();
      this.changeDetectorRef.detectChanges();
    });
  }

  private getAdaptiveGridContext(): AdaptiveViewportGridContext | null {
    if (!this.showViewportGrid) {
      return null;
    }

    const canvas = this.getGridCanvasElement();
    const viewport = this.getCanvasViewportElement();
    const root = this.canvasViewportShellRef?.nativeElement.querySelector('.component-builder__canvas-root') as HTMLElement | null;
    if (!canvas || !viewport || !root) {
      return null;
    }

    return {
      canvas,
      viewport,
      root,
      scale: this.canvasScale || 1,
      scrollLeft: this.canvasViewportScrollLeft,
      scrollTop: this.canvasViewportScrollTop,
      themeMode: this.currentCanvasThemeMode as AdaptiveGridThemeMode
    };
  }

  private getViewportRulerContext(): ViewportRulerRendererContext | null {
    if (!this.showViewportRulers) {
      return null;
    }

    const topCanvas = this.canvasViewportShellRef?.nativeElement.querySelector(
      '.component-builder__viewport-ruler-canvas--top'
    ) as HTMLCanvasElement | null;
    const leftCanvas = this.canvasViewportShellRef?.nativeElement.querySelector(
      '.component-builder__viewport-ruler-canvas--left'
    ) as HTMLCanvasElement | null;

    if (!topCanvas || !leftCanvas) {
      return null;
    }

    return {
      topCanvas,
      leftCanvas,
      scale: this.canvasScale || 1,
      scrollLeft: this.canvasViewportScrollLeft,
      scrollTop: this.canvasViewportScrollTop,
      logicalWidth: Math.max(this.canvasWidth, this.getCanvasViewportElement()?.scrollWidth ?? this.canvasWidth),
      logicalHeight: Math.max(this.canvasHeight, this.getCanvasViewportElement()?.scrollHeight ?? this.canvasHeight)
    };
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
